import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Input } from '@nextui-org/react';
import AsyncSelect from 'react-select/async';
import { AskerAddress } from 'common/src/utils/types';
import { COMMUNES } from 'common/src/constants/places/communes';
import { CITIES } from 'common/src/constants/places/cities';

interface Option {
  value: string;
  label: string;
  region?: string;
}

interface AskerAddressComponentProps {
  askerAddresses: AskerAddress[];
  isEmailValidated: boolean;
  onAddressChange: (addressId: number | null) => void;
  askerId: number | null;
}

interface NewAddressData {
  asker_id: number;
  address: string;
  commune: string;
  city: string;
}

export interface AskerAddressComponentRef {
  getNewAddressData: () => NewAddressData | null;
  getSelectedAddressId: () => number | null;
}

const AskerAddressComponent = forwardRef<
  AskerAddressComponentRef,
  AskerAddressComponentProps
>(({ askerAddresses, isEmailValidated, onAddressChange, askerId }, ref) => {
  const [showAddressInput, setShowAddressInput] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    null
  );
  const [address, setAddress] = useState('');
  const [selectedCity, setSelectedCity] = useState<Option | null>(null);
  const [selectedCommune, setSelectedCommune] = useState<Option | null>(null);

  useEffect(() => {
    if (askerAddresses.length > 0 && !selectedAddressId) {
      handleAddressSelect(askerAddresses[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askerAddresses]);

  const handleAddressSelect = (addressId: number) => {
    setSelectedAddressId(addressId);
    const selectedAddress = askerAddresses.find(
      (addr) => addr.id === addressId
    );
    if (selectedAddress) {
      setAddress(selectedAddress.address);
      setSelectedCity({
        value: selectedAddress.city || '',
        label: selectedAddress.city || '',
      });
      setSelectedCommune({
        value: selectedAddress.commune,
        label: selectedAddress.commune,
      });
      onAddressChange(addressId);
    }
    setShowAddressInput(false);
  };

  const handleCreateNewAddress = () => {
    setShowAddressInput(true);
    setSelectedAddressId(null);
    setAddress('');
    setSelectedCity(null);
    setSelectedCommune(null);
    onAddressChange(null);
  };

  const handleUseExistingAddress = () => {
    setShowAddressInput(false);
    if (askerAddresses.length > 0) {
      handleAddressSelect(askerAddresses[0].id);
    }
  };

  const loadCityOptions = (
    inputValue: string,
    callback: (options: Option[]) => void
  ) => {
    setTimeout(() => {
      const filteredOptions = CITIES.filter((city) =>
        city.name.toLowerCase().includes(inputValue.toLowerCase())
      ).map((city) => ({
        value: city.name,
        label: city.name,
        region: city.region, // Add region to the option
      }));
      callback(filteredOptions);
    }, 300);
  };

  const loadCommuneOptions = (
    inputValue: string,
    callback: (options: Option[]) => void
  ) => {
    setTimeout(() => {
      let filteredCommunes = COMMUNES;

      if (selectedCity) {
        const cityRegion = (selectedCity as Option & { region: string }).region;
        filteredCommunes = COMMUNES.filter(
          (commune) => commune.region === cityRegion
        );
      }

      const filteredOptions = filteredCommunes
        .filter((commune) =>
          commune.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((commune) => ({
          value: commune.name,
          label: commune.name,
        }));
      callback(filteredOptions);
    }, 300);
  };

  const handleCityChange = (selectedOption: Option | null) => {
    setSelectedCity(selectedOption);
    setSelectedCommune(null);
  };

  const handleCommuneChange = (selectedOption: Option | null) => {
    setSelectedCommune(selectedOption);
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
  };

  const customStyles = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: (baseStyles: any) => ({
      ...baseStyles,
      zIndex: 3, // Increase this value if needed
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    menu: (baseStyles: any) => ({
      ...baseStyles,
      zIndex: 4, // This should be higher than the control's z-index
    }),
  };

  const getNewAddressData = (): NewAddressData | null => {
    if (!askerId || !selectedCity || !selectedCommune || !address) {
      return null;
    }
    return {
      asker_id: askerId,
      address,
      commune: selectedCommune.value,
      city: selectedCity.value,
    };
  };

  const getSelectedAddressId = () => selectedAddressId;

  useImperativeHandle(ref, () => ({
    getNewAddressData,
    getSelectedAddressId,
  }));

  return (
    <div className="space-y-4 relative">
      {askerAddresses.length > 0 && !showAddressInput ? (
        <div>
          <label
            htmlFor="address-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Dirección
          </label>
          <select
            id="address-select"
            value={selectedAddressId?.toString() || ''}
            onChange={(e) => handleAddressSelect(Number(e.target.value))}
            className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!isEmailValidated}
          >
            <option value="" disabled className="text-gray-500">
              Selecciona una dirección
            </option>
            {askerAddresses.map((addr) => (
              <option
                key={addr.id}
                value={addr.id.toString()}
                className="text-gray-700"
              >
                {addr.address}, {addr.commune}, {addr.city}
              </option>
            ))}
          </select>
          <div className="flex justify-end mt-2">
            <button
              onClick={handleCreateNewAddress}
              className="px-4 py-1 text-violet-800 underline rounded-md"
              disabled={!isEmailValidated}
            >
              Crear nueva dirección
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <div className="flex-1">
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadCityOptions}
                onChange={handleCityChange}
                value={selectedCity}
                placeholder="Ciudad"
                isDisabled={!isEmailValidated}
                styles={customStyles}
                className="z-30"
              />
            </div>
            <div className="flex-1">
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadCommuneOptions}
                onChange={handleCommuneChange}
                value={selectedCommune}
                placeholder="Comuna"
                key={selectedCity ? selectedCity.value : 'default'}
                isDisabled={!isEmailValidated}
                styles={customStyles}
                className="z-20"
              />
            </div>
          </div>
          <Input
            label="Dirección"
            name="address"
            value={address}
            onChange={handleAddressChange}
            isDisabled={!isEmailValidated}
          />
          {askerAddresses.length > 0 && (
            <p
              onClick={handleUseExistingAddress}
              className={`text-right underline cursor-pointer ${
                isEmailValidated ? 'text-violet-800' : 'text-gray-400'
              }`}
              style={{
                pointerEvents: isEmailValidated ? 'auto' : 'none',
              }}
            >
              Usar dirección existente
            </p>
          )}
        </>
      )}
    </div>
  );
});

export default AskerAddressComponent;
